<template>
  <div class="milestone-panel">
    <!-- 요약 바 -->
    <div class="summary-bar">
      <div class="summary-stats">
        <span class="stat">
          전체 <strong>{{ milestones.length }}</strong>
        </span>
        <span class="stat completed">
          완료 <strong>{{ completedCount }}</strong>
        </span>
        <span v-if="overdueCount > 0" class="stat overdue">
          지연 <strong>{{ overdueCount }}</strong>
        </span>
        <span class="stat">
          달성 <strong>{{ progressPercent }}%</strong>
        </span>
      </div>
      <div class="summary-actions">
        <el-radio-group v-model="viewMode" size="small">
          <el-radio-button value="grid">격자</el-radio-button>
          <el-radio-button value="list">목록</el-radio-button>
        </el-radio-group>
        <!-- 엑셀식 일괄 입력 (2026-08-03): 템플릿×수량배분으로 행렬을 미리 깔고 날짜만 입력 -->
        <el-button v-if="canEdit && viewMode === 'grid' && !bulkEdit.active"
                   type="warning" size="small" plain @click="enterBulkEdit">일괄 입력</el-button>
        <template v-if="bulkEdit.active">
          <el-button size="small" @click="fillFromAverage"
                     :disabled="!Object.keys(bulkEdit.avgOffsets).length">
            평균으로 채우기
          </el-button>
          <el-button type="primary" size="small" :loading="bulkEdit.saving"
                     :disabled="!bulkChangeCount" @click="saveBulkEdit">
            저장 ({{ bulkChangeCount }})
          </el-button>
          <el-button size="small" @click="bulkEdit.active = false">취소</el-button>
        </template>
        <el-button v-if="canEdit && !bulkEdit.active" type="primary" size="small" @click="emit('add')">
          마일스톤 추가
        </el-button>
      </div>
    </div>

    <!-- 진행 바 -->
    <div v-if="milestones.length > 0" class="progress-track">
      <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
      <div
        v-for="(m, idx) in milestones"
        :key="m.id"
        class="progress-dot"
        :class="getDotClass(m)"
        :style="{ left: getDotPosition(idx) + '%' }"
        :title="`${m.name} (${m.planned_date})`"
        @click="scrollToRow(m.id)"
      />
    </div>

    <!-- 엑셀식 일괄 입력 모드 (2026-08-03): 행=색상, 열=템플릿 단계 (보드와 동일 배치) —
         이미 있는 정보로 행렬을 만들어 두고 각 칸은 날짜만 입력 -->
    <div v-if="bulkEdit.active" class="ms-grid-wrap">
      <div class="hint" style="margin-bottom:6px">
        {{ bulkEdit.stageSource }} × 수량배분 색상 — 날짜를 입력한 칸만 저장됩니다.
        기존 일정은 미리 채워져 있고 수정하면 함께 저장됩니다.
        <template v-if="Object.keys(bulkEdit.avgOffsets).length">
          · [평균으로 채우기]는 과거 완료 실적의 단계별 평균(납기 전 일수)으로 빈 칸을 제안합니다.
        </template>
      </div>
      <table class="ms-grid">
        <thead>
          <tr>
            <th class="stage-col">색상</th>
            <th v-for="base in bulkColumns" :key="base">
              {{ base }}
              <span v-if="bulkEdit.avgOffsets[base]" class="avg-hint"
                    :title="`과거 ${bulkEdit.avgOffsets[base].samples}건 평균`">
                D-{{ bulkEdit.avgOffsets[base].days_before }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in bulkEdit.rows" :key="row.group">
            <td class="stage-col">{{ row.group }}</td>
            <td v-for="base in bulkColumns" :key="base" class="bulk-cell">
              <el-date-picker v-model="row.cells[base].date" type="date"
                              value-format="YYYY-MM-DD" size="small"
                              :placeholder="row.cells[base].id ? '' : '-'"
                              style="width: 110px"
                              :class="{ 'cell-changed': isCellChanged(row.cells[base]) }" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 격자 뷰: 행=색상, 열=단계 — 스케줄 보드와 동일 배치 (2026-08-03 사이먼)
         빈 칸 클릭=추가, [+ 색상]=재발주 차수 행 추가(기존 행 날짜 복사) -->
    <div v-if="!bulkEdit.active && viewMode === 'grid' && milestones.length > 0" class="ms-grid-wrap">
      <table class="ms-grid">
        <thead>
          <tr>
            <th class="stage-col">색상</th>
            <th v-for="base in gridColumns" :key="base">{{ base }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group in gridRowGroups" :key="group">
            <td class="stage-col">{{ group }}</td>
            <td v-for="base in gridColumns" :key="base"
                class="ms-cell" :class="cellClass(cellOf(group, base))"
                @click="onCellClick(group, base)">
              <template v-if="cellOf(group, base)">
                <span class="cell-date">{{ formatDate(cellOf(group, base).planned_date) }}</span>
                <span class="cell-mark">{{ cellMark(cellOf(group, base)) }}</span>
              </template>
              <span v-else-if="canEdit" class="cell-plus">+</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="hint" style="margin-top:4px">
        칸 클릭 = {{ canEdit ? '수정 · 빈 칸은 추가' : '이력' }} · ✓완료 · !지연
        <template v-if="canEdit">
          · <el-button type="primary" link size="small" @click="openColumnAdd">+ 색상 행 추가</el-button>
          (기존 행 날짜 복사 가능)
        </template>
      </div>
    </div>

    <!-- 빈 칸 추가 (2026-08-03) -->
    <el-dialog v-model="cellAdd.visible" title="마일스톤 추가" width="340px">
      <div class="cell-add-body">
        <div class="cell-add-target">{{ cellAdd.base }} <el-tag size="small">{{ cellAdd.col }}</el-tag></div>
        <el-date-picker v-model="cellAdd.planned_date" type="date"
                        value-format="YYYY-MM-DD" placeholder="예정일" style="width: 100%" />
      </div>
      <template #footer>
        <el-button @click="cellAdd.visible = false">취소</el-button>
        <el-button type="primary" :disabled="!cellAdd.planned_date" @click="submitCellAdd">추가</el-button>
      </template>
    </el-dialog>

    <!-- 색상 행 추가 (2026-08-03) — 재발주 차수를 행 단위로 (배치=보드와 동일) -->
    <el-dialog v-model="colAdd.visible" title="색상 행 추가" width="420px">
      <el-form label-width="100px">
        <el-form-item label="색상">
          <el-select v-model="colAdd.tokens" multiple filterable allow-create
                     default-first-option style="width:100%"
                     placeholder="예: DG#2 (재발주 2차)">
            <el-option v-for="c in colorOptions" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>
        <el-form-item label="날짜 복사">
          <el-select v-model="colAdd.copyFrom" clearable style="width:100%"
                     placeholder="기존 색상 행에서 예정일 복사 (선택)">
            <el-option v-for="g in gridRowGroups" :key="g" :label="g" :value="g" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="colAdd.copyFrom" label="일수 시프트">
          <el-input-number v-model="colAdd.offset" :step="1" style="width: 140px" />
          <span class="hint" style="margin-left:8px">재발주는 보통 +N일</span>
        </el-form-item>
        <div class="hint" style="margin-left:12px">
          복사 없이 추가하면 빈 행이 생기고, 각 칸을 클릭해 채웁니다.
        </div>
      </el-form>
      <template #footer>
        <el-button @click="colAdd.visible = false">취소</el-button>
        <el-button type="primary" :disabled="!colAdd.tokens.length" @click="submitColumnAdd">추가</el-button>
      </template>
    </el-dialog>

    <!-- 컴팩트 테이블 -->
    <el-table
      v-if="!bulkEdit.active && viewMode === 'list' && milestones.length > 0"
      :data="milestones"
      size="small"
      border
      stripe
      :row-class-name="rowClassName"
      class="milestone-table"
    >
      <el-table-column type="index" label="#" width="40" align="center" />
      <el-table-column prop="name" label="마일스톤" min-width="120">
        <template #default="{ row }">
          <span :id="`ms-${row.id}`" class="ms-name">{{ row.name }}</span>
        </template>
      </el-table-column>
      <el-table-column label="상태" width="80" align="center">
        <template #default="{ row }">
          <span class="status-dot" :class="getDotClass(row)" />
          <span class="status-text">{{ getStatusLabel(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="예정일" width="100" align="center">
        <template #default="{ row }">
          {{ formatDate(row.planned_date) }}
        </template>
      </el-table-column>
      <el-table-column label="완료일" width="100" align="center">
        <template #default="{ row }">
          <span v-if="row.actual_date">{{ formatDate(row.actual_date) }}</span>
          <span v-else class="dday-label" :class="{ 'is-overdue': row.is_overdue }">
            {{ getDdayLabel(row) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="차이" width="60" align="center">
        <template #default="{ row }">
          <span v-if="row.actual_date" :class="getVarianceClass(row)">
            {{ getVarianceDays(row) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="assigned_to_name" label="담당" width="70" align="center">
        <template #default="{ row }">
          {{ row.assigned_to_name || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="" width="130" align="center">
        <template #default="{ row }">
          <el-button
            v-if="canEdit && row.status !== 'COMPLETED' && row.status !== 'SKIPPED'"
            type="success" link size="small" @click="emit('complete', row)"
          >완료</el-button>
          <el-button v-if="canEdit" type="primary" link size="small"
                     @click="emit('edit', row)">수정</el-button>
          <el-button link size="small" @click="emit('history', row)">이력</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="!bulkEdit.active && milestones.length === 0" description="마일스톤이 없습니다">
      <el-button v-if="canEdit" type="warning" plain @click="enterBulkEdit">일괄 입력</el-button>
      <el-button v-if="canEdit" type="primary" @click="emit('add')">
        마일스톤 추가
      </el-button>
    </el-empty>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import axiosInstance from '@/api/axios'

const props = defineProps({
  milestones: {
    type: Array,
    default: () => []
  },
  canEdit: {
    type: Boolean,
    default: false
  },
  // 색상 열 추가 옵션 (수량배분 색상 — 부모가 전달, 2026-08-03)
  colorOptions: {
    type: Array,
    default: () => []
  },
  // 일괄 입력 스캐폴드 조회용 (2026-08-03)
  mainRequestId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits(['complete', 'add', 'edit', 'history', 'cells-add', 'bulk-save'])

import { ref } from 'vue'

// 색상 그룹 (2026-08-03): colors 필드 정본, 이름 접미사 파싱은 구 데이터 폴백
const colorLabel = (m) => {
  if (m.colors && m.colors.length) return m.colors.join('·')
  const match = (m.name || '').match(/^(.+?)\s*\(([^)]+)\)\s*$/)
  return match ? match[2].trim() : ''
}

// 컬러 구분 마일스톤이 있으면 격자, 아니면 목록이 기본
const hasColorSplit = computed(() => props.milestones.some(m => colorLabel(m)))
const viewMode = ref('list')
import { watch } from 'vue'
watch(hasColorSplit, v => { viewMode.value = v ? 'grid' : 'list' }, { immediate: true })

const splitName = (name) => {
  const m = name.match(/^(.+?)\s*\(([^)]+)\)\s*$/)
  return m ? { base: m[1].trim(), col: m[2].trim() } : { base: name.trim(), col: '전체' }
}

const groupOf = (m) => colorLabel(m) || '전체'

// ── 격자 배치 = 스케줄 보드와 동일 (2026-08-03 사이먼): 행=색상, 열=단계 ──
// 같은 데이터를 두 화면이 다른 방향으로 보여줘 혼란 → 보드(주간 스케줄 엑셀
// 배치)에 맞춰 통일. 이전: 행=단계/열=색상 (전치 상태였음).

// 빈 행(로컬 초안): 복사 없이 추가한 색상 행 — 칸을 채우면 데이터로 실체화
const draftCols = ref([])

// 열: 단계(base) — sequence 순
const gridColumns = computed(() => {
  const seen = new Map()
  for (const m of props.milestones) {
    const base = splitName(m.name).base
    if (!seen.has(base)) seen.set(base, m.sequence ?? 0)
  }
  return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([b]) => b)
})

// 행: 색상 그룹 ('전체' 맨 앞)
const gridRowGroups = computed(() => {
  const rows = []
  for (const m of props.milestones) {
    const g = groupOf(m)
    if (!rows.includes(g)) rows.push(g)
  }
  for (const c of draftCols.value) if (!rows.includes(c)) rows.push(c)
  rows.sort((a, b) => (a === '전체' ? -1 : b === '전체' ? 1 : 0))
  return rows
})

const cellOf = (group, base) =>
  props.milestones.find(
    m => splitName(m.name).base === base && groupOf(m) === group) || null

// ── 격자 직접 입력 (2026-08-03) ──────────────────────────────
const tokensOf = (col) => col === '전체' ? [] : col.split('·').map(t => t.trim()).filter(Boolean)

const cellAdd = ref({ visible: false, base: '', col: '', planned_date: '' })
const colAdd = ref({ visible: false, tokens: [], copyFrom: '', offset: 0 })

const onCellClick = (group, base) => {
  const cell = cellOf(group, base)
  if (cell) {
    emit(props.canEdit ? 'edit' : 'history', cell)
  } else if (props.canEdit) {
    cellAdd.value = { visible: true, base, col: group, planned_date: '' }
  }
}

const submitCellAdd = () => {
  const c = cellAdd.value
  emit('cells-add', [{ base: c.base, colors: tokensOf(c.col), planned_date: c.planned_date }])
  cellAdd.value.visible = false
}

const openColumnAdd = () => {
  colAdd.value = { visible: true, tokens: [], copyFrom: '', offset: 0 }
}

const shiftDate = (dateStr, days) => {
  if (!dateStr) return dateStr
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + (Number(days) || 0))
  return d.toISOString().substring(0, 10)
}

// ── 엑셀식 일괄 입력 (2026-08-03) ────────────────────────────
// 행=템플릿 단계(브랜드→전사→코드기본), 열=수량배분 색상 — 날짜만 입력.
// 과거 완료 실적의 단계별 평균(납기 전 일수)으로 빈 칸 제안 가능.
const bulkEdit = ref({
  active: false, saving: false, rows: [],
  stageSource: '', avgOffsets: {}, deliveryDate: '', columns: [],
})

const bulkColumns = computed(() => bulkEdit.value.columns)

const findExisting = (base, col) => {
  for (const m of props.milestones) {
    if (splitName(m.name).base !== base) continue
    const label = colorLabel(m) || '전체'
    if (label === col) return m
  }
  return null
}

const enterBulkEdit = async () => {
  if (!props.mainRequestId) return
  let scaffold = { stages: [], colors: [], avg_offsets: {}, delivery_date: '', stage_source: '' }
  try {
    const { data } = await axiosInstance.get(
      `/production/main-requests/${props.mainRequestId}/milestone-grid-scaffold/`)
    scaffold = data
  } catch { /* 스캐폴드 실패 시 기존 데이터만으로 편집 */ }

  // 행: 기존 색상 그룹 + 수량배분 색상 중 아직 그룹에 안 묶인 것. 공통은 '전체'.
  // (배치 = 스케줄 보드와 동일: 행=색상, 열=단계 — 2026-08-03 사이먼)
  const existingGroups = []
  for (const m of props.milestones) {
    const label = colorLabel(m)
    if (label && !existingGroups.includes(label)) existingGroups.push(label)
  }
  const covered = new Set(existingGroups.flatMap(
    c => c.split('·').map(t => t.split('#')[0].trim())))
  const extraColors = (scaffold.colors || []).filter(c => !covered.has(c))
  const hasCommon = props.milestones.some(m => !colorLabel(m))
  const grps = [...existingGroups, ...extraColors]
  const groups = (hasCommon || !grps.length) ? ['전체', ...grps] : grps

  // 열: 템플릿 단계 순서 + 기존에만 있는 단계
  const bases = (scaffold.stages || []).map(s => s.base)
  for (const m of props.milestones) {
    const b = splitName(m.name).base
    if (!bases.includes(b)) bases.push(b)
  }

  const rows = groups.map(group => {
    const cells = {}
    for (const base of bases) {
      const found = findExisting(base, group)
      cells[base] = found
        ? { id: found.id, date: found.planned_date, orig: found.planned_date }
        : { id: null, date: '', orig: '' }
    }
    return { group, cells }
  })

  bulkEdit.value = {
    active: true, saving: false, rows, columns: bases,
    stageSource: scaffold.stage_source || '기존 단계',
    avgOffsets: scaffold.avg_offsets || {},
    deliveryDate: scaffold.delivery_date || '',
  }
}

const isCellChanged = (cell) =>
  cell.id ? (cell.date && cell.date !== cell.orig) : !!cell.date

const bulkChangeCount = computed(() => {
  let n = 0
  for (const row of bulkEdit.value.rows) {
    for (const col of bulkEdit.value.columns) {
      if (isCellChanged(row.cells[col])) n += 1
    }
  }
  return n
})

const fillFromAverage = () => {
  // 열=단계 배치: 각 단계 열의 평균으로 그 열의 빈 칸을 전 색상 행에 제안
  const b = bulkEdit.value
  if (!b.deliveryDate) return
  for (const base of b.columns) {
    const avg = b.avgOffsets[base]
    if (!avg) continue
    const suggested = shiftDate(b.deliveryDate, -avg.days_before)
    for (const row of b.rows) {
      const cell = row.cells[base]
      if (!cell.id && !cell.date) cell.date = suggested
    }
  }
}

const saveBulkEdit = () => {
  const b = bulkEdit.value
  const creates = []
  const updates = []
  for (const row of b.rows) {
    for (const col of b.columns) {
      const cell = row.cells[col]
      if (!isCellChanged(cell)) continue
      if (cell.id) updates.push({ id: cell.id, planned_date: cell.date })
      else creates.push({ base: col, colors: tokensOf(row.group), planned_date: cell.date })
    }
  }
  if (!creates.length && !updates.length) return
  b.saving = true
  emit('bulk-save', {
    creates, updates,
    done: (ok) => { b.saving = false; if (ok) b.active = false },
  })
}

const submitColumnAdd = () => {
  // 색상 행 추가 (배치 전환 후: 행=색상) — 기존 행의 단계·예정일 복사(+시프트)
  const c = colAdd.value
  const label = c.tokens.join('·')
  if (c.copyFrom) {
    const items = gridColumns.value
      .map(base => ({ base, src: cellOf(c.copyFrom, base) }))
      .filter(x => x.src)
      .map(x => ({
        base: x.base, colors: c.tokens,
        planned_date: shiftDate(x.src.planned_date, c.offset),
      }))
    if (!items.length) return
    emit('cells-add', items)
  } else {
    draftCols.value.push(label)  // 빈 행 — 칸 클릭으로 채움
  }
  colAdd.value.visible = false
}

// (구 gridRows — 행=단계 배치는 제거. 행=색상은 gridRowGroups + cellOf 사용)

const cellClass = (m) => {
  if (!m) return 'cell-empty'
  if (m.status === 'COMPLETED') return 'cell-done'
  if (m.is_overdue) return 'cell-overdue'
  if (m.status === 'IN_PROGRESS') return 'cell-active'
  return 'cell-pending'
}

const cellMark = (m) => {
  if (m.status === 'COMPLETED') return '✓'
  if (m.is_overdue) return '!'
  return ''
}

const completedCount = computed(() =>
  props.milestones.filter(m => m.status === 'COMPLETED').length
)

const overdueCount = computed(() =>
  props.milestones.filter(m => m.is_overdue).length
)

const progressPercent = computed(() => {
  if (props.milestones.length === 0) return 0
  return Math.round(completedCount.value / props.milestones.length * 100)
})

const getDotPosition = (idx) => {
  if (props.milestones.length <= 1) return 50
  return (idx / (props.milestones.length - 1)) * 100
}

const getDotClass = (m) => {
  if (m.status === 'COMPLETED') return 'dot-completed'
  if (m.is_overdue) return 'dot-overdue'
  if (m.status === 'IN_PROGRESS') return 'dot-active'
  if (m.status === 'SKIPPED') return 'dot-skipped'
  return 'dot-pending'
}

const getStatusLabel = (m) => {
  if (m.status === 'COMPLETED') return '완료'
  if (m.is_overdue) return '지연'
  if (m.status === 'IN_PROGRESS') return '진행'
  if (m.status === 'SKIPPED') return '생략'
  return '대기'
}

const getDdayLabel = (m) => {
  if (m.status === 'COMPLETED' || m.status === 'SKIPPED') return ''
  if (m.days_remaining === undefined) return ''
  return m.days_remaining >= 0 ? `D-${m.days_remaining}` : `D+${Math.abs(m.days_remaining)}`
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  // YYYY-MM-DD → MM/DD
  const parts = dateStr.split('-')
  if (parts.length === 3) return `${parts[1]}/${parts[2]}`
  return dateStr
}

const getVarianceDays = (m) => {
  if (!m.actual_date || !m.planned_date) return ''
  const planned = new Date(m.planned_date)
  const actual = new Date(m.actual_date)
  const diff = Math.round((actual - planned) / (1000 * 60 * 60 * 24))
  if (diff === 0) return '0'
  return diff > 0 ? `+${diff}` : `${diff}`
}

const getVarianceClass = (m) => {
  const v = getVarianceDays(m)
  if (v.startsWith('+')) return 'variance-late'
  if (v.startsWith('-')) return 'variance-early'
  return ''
}

const rowClassName = ({ row }) => {
  if (row.is_overdue) return 'row-overdue'
  if (row.status === 'COMPLETED') return 'row-completed'
  if (row.status === 'IN_PROGRESS') return 'row-active'
  return ''
}

const scrollToRow = (id) => {
  const el = document.getElementById(`ms-${id}`)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
}
</script>

<style scoped>
.milestone-panel {
  padding: 12px;
}

/* 요약 바 */
.summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.summary-stats {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #606266;
}
.stat strong {
  margin-left: 2px;
}
.stat.completed strong { color: #67C23A; }
.stat.overdue strong { color: #F56C6C; }

/* 진행 바 */
.progress-track {
  position: relative;
  height: 24px;
  background: #EBEEF5;
  border-radius: 12px;
  margin-bottom: 16px;
}
.progress-fill {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  background: linear-gradient(90deg, #67C23A, #85ce61);
  border-radius: 12px;
  transition: width 0.3s;
}
.progress-dot {
  position: absolute;
  top: 50%;
  width: 10px; height: 10px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #fff;
  cursor: pointer;
  z-index: 1;
  transition: transform 0.15s;
}
.progress-dot:hover {
  transform: translate(-50%, -50%) scale(1.5);
}
.dot-completed { background: #67C23A; }
.dot-active { background: #409EFF; }
.dot-overdue { background: #F56C6C; }
.dot-skipped { background: #C0C4CC; }
.dot-pending { background: #DCDFE6; }

/* 테이블 */
.milestone-table {
  font-size: 13px;
}

.ms-name {
  font-weight: 500;
}

.status-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
.status-text {
  font-size: 12px;
  vertical-align: middle;
}

.dday-label {
  color: #909399;
  font-size: 12px;
}
.dday-label.is-overdue {
  color: #F56C6C;
  font-weight: bold;
}

.variance-late { color: #F56C6C; font-weight: bold; }
.variance-early { color: #67C23A; }

/* 격자 뷰 */
.summary-actions { display: flex; gap: 8px; align-items: center; }
.ms-grid-wrap { overflow-x: auto; }
.ms-grid { border-collapse: collapse; width: 100%; font-size: var(--sw-font-sm, 13px); }
.ms-grid th, .ms-grid td {
  border: 1px solid #EBEEF5; padding: 6px 8px; text-align: center; white-space: nowrap;
}
.ms-grid th { background: #F5F7FA; color: #606266; font-weight: 600; }
.stage-col { text-align: left; font-weight: 500; min-width: 90px; }
.ms-cell { cursor: pointer; }
.ms-cell:hover { outline: 2px solid #a0cfff; outline-offset: -2px; }
.cell-empty { background: #FAFAFA; cursor: default; }
.cell-done { background: #F0F9EB; color: #67C23A; }
.cell-overdue { background: #FEF0F0; color: #F56C6C; font-weight: 600; }
.cell-active { background: #ECF5FF; color: #409EFF; }
.cell-mark { margin-left: 3px; font-weight: 700; }
.hint { color: #909399; font-size: 12px; }

/* 행 색상 */
:deep(.row-overdue) {
  background-color: #FEF0F0 !important;
}
:deep(.row-completed) {
  color: #909399;
}
:deep(.row-active) {
  background-color: #ECF5FF !important;
}

/* 격자 직접 입력 (2026-08-03) */
.cell-plus { color: #c0c4cc; font-weight: 700; }
.ms-cell:hover .cell-plus { color: #409EFF; }
.add-col-th { min-width: 60px; }
.cell-add-body { display: flex; flex-direction: column; gap: 10px; }
.cell-add-target { font-weight: 600; }

/* 일괄 입력 (2026-08-03) */
.bulk-cell { padding: 3px 4px !important; }
.avg-hint { display: block; font-size: 11px; color: #E6A23C; font-weight: 400; }
.cell-changed :deep(.el-input__wrapper) { box-shadow: 0 0 0 1px #E6A23C inset; }
</style>
